<template>
    <PageHeader :title="$t('player_selector.title')" backRoute="home" />

    <div class="px-4 py-2">
        <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-bold text-white">{{ $t('player_selector.step') }}</span>
            <span class="text-sm font-bold text-white"
                >{{ stepInfo.current }}/{{ stepInfo.total ?? 4 }}</span
            >
        </div>
        <div
            class="bg-stroke-1 h-1 w-full"
            style="clip-path: polygon(4px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)"
        >
            <div
                class="bg-progress-gradient transition-300 h-1 rounded-full"
                :style="{
                    'clip-path': 'polygon(4px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)',
                    width: `${(stepInfo.current / (stepInfo.total ?? 4)) * 100}%`,
                }"
            ></div>
        </div>
    </div>

    <div class="flex flex-1 flex-col overflow-hidden">
        <TabBar v-model="activeTab" :tabs="tabs" class="mx-4" />

        <div class="flex-1 overflow-y-auto p-4">
            <div v-if="activeTab === 'friends'" class="relative space-y-4">
                <p class="text-lg font-extrabold text-white uppercase">{{ $t('player_selector.play_with_friends') }}</p>

                <div class="space-y-3">
                    <div>
                        <FriendSearchInput
                            v-model="friendSearchQuery"
                            :placeholder="$t('friends.search_placeholder')"
                            @update:modelValue="debouncedSearch"
                        />
                    </div>

                    <div v-if="!friendSearchQuery.trim()" class="space-y-2">
                        <FriendItem
                            v-for="friendship in authStore.friends"
                            :key="friendship.id"
                            :friend="friendship.friend"
                            :show-button="true"
                            :button-text="
                                matchProfileIds.has(friendship.friend.id)
                                    ? $t('player_selector.added')
                                    : $t('player_selector.add')
                            "
                            :loading="loadingFriendId === friendship.friend.id"
                            :show-online-indicator="true"
                            :is-online="friendship.friend.is_online"
                            @action="sendGameInviteToFriend"
                        />
                    </div>

                    <div v-if="friendSearchQuery.trim()" class="space-y-2">
                        <div
                            v-if="searchResults.length === 0 && !isSearching"
                            class="pb-4 font-semibold text-white"
                        >
                            {{ $t('player_selector.no_user_found') }}
                        </div>

                        <div v-if="isSearching" class="text-titles py-4 text-center">
                            {{ $t('player_selector.searching') }}
                        </div>

                        <div v-if="searchResults.length > 0">
                            <Panel
                                v-for="friend in searchResults"
                                :key="friend.id"
                                class="w-full text-white"
                            >
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center space-x-3">
                                        <div>
                                            <Avatar :src="friend.avatar" :size="10" />
                                        </div>
                                        <div>
                                            <p class="text-sm font-semibold text-white">
                                                {{ friend.tag_name }}
                                            </p>
                                            <p class="text-titles text-sm">{{ friend.name }}</p>
                                        </div>
                                    </div>
                                    <button
                                        v-if="!matchProfileIds.has(friend.id)"
                                        @click="sendGameInviteToFriend(friend)"
                                        :disabled="isUpdating"
                                        class="rounded-full p-2 text-red-500 transition-colors hover:bg-green-50 hover:text-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        :title="$t('player_selector.add_to_match')"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="2"
                                            stroke="currentColor"
                                            class="size-5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M12 4.5v15m7.5-7.5h-15"
                                            />
                                        </svg>
                                    </button>
                                    <div
                                        v-else
                                        class="bg-[#303030] px-3 py-1 text-sm font-semibold text-white uppercase"
                                        :title="$t('player_selector.already_added')"
                                    >
                                        {{ $t('player_selector.invite_sent') }}
                                    </div>
                                </div>
                            </Panel>
                        </div>
                    </div>

                    <!-- <button
                        @click="sendGameInvite"
                        :disabled="isUpdating"
                        class="bg-bg flex w-full"
                    >
                        <span class="inline-block bg-red-500 p-4">
                            <svg
                                class="size-5"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M21.7225 2.27734L14.058 22.4882L9.87585 14.125L1.51172 9.9429L21.7225 2.27734ZM11.8379 13.5769L13.8059 17.5125L18.2767 5.72328L6.48748 10.1947L10.4236 12.1628L13.7753 8.81038L15.1896 10.2244L11.8379 13.5769Z"
                                    fill="white"
                                />
                            </svg>
                        </span>
                        <span
                            class="border-stroke-1 flex flex-1 items-center border border-l-0 px-5 font-bold text-white"
                            >Share game invite
                            <svg
                                width="9"
                                height="14"
                                viewBox="0 0 9 14"
                                fill="none"
                                class="ml-auto"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                                    fill="#797979"
                                />
                            </svg>
                        </span>
                    </button>

                    <button @click="scanQRCode" :disabled="isUpdating" class="bg-bg flex w-full">
                        <span class="inline-block bg-red-500 p-4">
                            <svg
                                class="size-5"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 11H14M16 17H17M11 14V17H13M14 14H17V12M1 1H7V7H1V1ZM1 11H7V17H1V11ZM11 1H17V7H11V1Z"
                                    stroke="white"
                                    stroke-width="2"
                                />
                            </svg>
                        </span>
                        <span
                            class="border-stroke-1 flex flex-1 items-center border border-l-0 px-5 font-bold text-white"
                            >Scan the QR-code
                            <svg
                                width="9"
                                height="14"
                                viewBox="0 0 9 14"
                                fill="none"
                                class="ml-auto"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                                    fill="#797979"
                                />
                            </svg>
                        </span>
                    </button> -->
                </div>
                <!-- <div class="absolute w-full h-full bg-bg/80 top-0 left-0" v-if="maxPlayersReached"></div> -->
            </div>

            <div v-if="activeTab === 'guest'" class="space-y-4">
                <p class="text-lg font-extrabold text-white uppercase">{{ $t('player_selector.add_guest_heading') }}</p>

                <div class="space-y-3">
                    <div>
                        <input
                            v-model="guestName"
                            type="text"
                            :placeholder="$t('player_selector.guest_placeholder')"
                            class="border-stroke-1 bg-card w-full border p-3 focus:border-red-500 focus:ring-2 focus:ring-red-500"
                            @keyup.enter="addGuest"
                        />
                    </div>

                    <DiagonalCutButton
                        @click="addGuest"
                        :disabled="!guestName.trim() || isUpdating"
                        variant="primary"
                        cut-position="left-top"
                    >
                        <span v-if="isUpdating">{{ $t('player_selector.adding') }}</span>
                        <span v-else>{{ $t('player_selector.add') }}</span>
                    </DiagonalCutButton>
                </div>
            </div>

            <div v-if="activeTab === 'dartbot'" class="relative space-y-4">
                <p class="text-lg font-extrabold text-white uppercase">{{ $t('player_selector.play_vs_bot') }}</p>

                <div class="grid grid-cols-2 gap-4">
                    <Panel
                        size="full"
                        v-for="(bot, index) in dartbots"
                        :key="bot.id"
                        @click="addDartbot(bot)"
                        bgClass="bg-grey-gradient-strokes"
                        :corners="
                            index % 2 === 0 ? 'clip-both-right-corners' : 'clip-both-left-corners'
                        "
                    >
                        <div class="grid grid-cols-2 items-stretch gap-3">
                            <Avatar :src="bot.avatar" :size="14" />
                            <div class="flex h-full flex-col justify-between">
                                <div class="text-end text-4xl leading-5 text-red-500">+</div>
                                <div
                                    class="line-clamp-1 self-start text-start text-xs font-bold text-ellipsis"
                                >
                                    {{ bot.name }}
                                </div>
                            </div>
                        </div>
                        <div class="mt-4 flex flex-col items-start gap-3 text-xs">
                            <div class="bg-bg">
                                <div class="border-1 border-[#2B2B2B] p-2 font-bold">
                                    {{ bot.difficulty }}
                                </div>
                            </div>
                        </div>
                    </Panel>
                </div>
                <div
                    class="bg-bg absolute top-0 left-0 h-full w-full opacity-80"
                    v-if="
                        matchStore.hasBotProfile ||
                        maxPlayersReached ||
                        !currentGameTypeSettings.botsAllowed
                    "
                ></div>
            </div>
        </div>
    </div>

    <div class="border-stroke-1 bg-card mt-auto border-t-1 pb-4">
        <div class="px-4 py-3">
            <div
                class="mb-3 flex items-center justify-end"
                v-if="matchStore.setup.matchtype_id && currentGameTypeSettings.teamPlayAllowed"
            >
                <div class="flex h-8 w-16 items-center">
                    <button
                        @click="setPlayMode('singles')"
                        :class="[
                            'flex h-8 w-8 flex-none items-center justify-center',
                            playMode === 'singles'
                                ? 'bg-red-500'
                                : 'border-stroke-1 border bg-zinc-700',
                        ]"
                    >
                        <svg
                            class="size-5"
                            viewBox="0 0 16 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16 12.5V16.5L14 17.5V13.5H2V17.5L0 16.5V12.5L1 11.5H15L16 12.5ZM11 5.5C11 3.84315 9.65685 2.5 8 2.5C6.34315 2.5 5 3.84315 5 5.5C5 7.15685 6.34315 8.5 8 8.5C9.65685 8.5 11 7.15685 11 5.5ZM13 5.5C13 8.26142 10.7614 10.5 8 10.5C5.23858 10.5 3 8.26142 3 5.5C3 2.73858 5.23858 0.5 8 0.5C10.7614 0.5 13 2.73858 13 5.5Z"
                                fill="white"
                            />
                        </svg>
                    </button>

                    <button
                        @click="setPlayMode('teams')"
                        :class="[
                            'border-stroke-1 flex h-8 w-8 flex-none items-center justify-center border',
                            playMode === 'teams' ? 'bg-red-500' : 'bg-zinc-700',
                        ]"
                    >
                        <svg
                            class="size-7"
                            viewBox="0 0 24 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M17 16.5V20.5L15 21.5V17.5H5V21.5L3 20.5V16.5L4 15.5H16L17 16.5ZM12 10.5C12 9.39543 11.1046 8.5 10 8.5C8.89543 8.5 8 9.39543 8 10.5C8 11.6046 8.89543 12.5 10 12.5C11.1046 12.5 12 11.6046 12 10.5ZM14 10.5C14 12.7091 12.2091 14.5 10 14.5C7.79086 14.5 6 12.7091 6 10.5C6 8.29086 7.79086 6.5 10 6.5C12.2091 6.5 14 8.29086 14 10.5Z"
                                fill="white"
                            />
                            <path
                                d="M22 13.5V17.5L20 18.5V14.5H15V12.5H21L22 13.5ZM15 3.5C17.2091 3.5 19 5.29086 19 7.5C19 9.70914 17.2091 11.5 15 11.5V9.5C16.1046 9.5 17 8.60457 17 7.5C17 6.39543 16.1046 5.5 15 5.5V3.5Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <div v-if="!isTeamMode" class="flex space-x-3">
                <PlayerCard
                    v-for="matchProfile in sortedProfiles"
                    :key="matchProfile.id"
                    :player="matchProfile"
                    :current-user-id="authStore.profile.id"
                    :disabled="isUpdating"
                    @remove="removeMatchProfile"
                />

                <div v-if="sortedProfiles.length === 0" class="text-titles py-4 text-center">
                    {{ $t('player_selector.no_players') }}
                </div>
            </div>

            <div v-if="isTeamMode" class="space-y-4">
                <div v-if="unassignedPlayers.length > 0" class="space-y-2">
                    <h3 class="text-center text-sm font-bold text-white uppercase">
                        {{ $t('player_selector.unassigned') }}
                    </h3>
                    <div class="flex flex-wrap justify-center gap-2">
                        <PlayerCard
                            v-for="player in unassignedPlayers"
                            :key="player.id"
                            :player="player"
                            :current-user-id="authStore.profile.id"
                            :disabled="isUpdating"
                            :clickable="true"
                            :show-status="false"
                            :image-title="$t('player_selector.assign_to_team_prompt')"
                            :center-items="true"
                            @click="showTeamAssignmentModal"
                            @remove="removeMatchProfile"
                        />
                    </div>
                </div>

                <div class="flex items-stretch justify-between gap-4">
                    <div class="flex-1 space-y-2">
                        <h3
                            class="cursor-pointer text-center text-sm font-bold text-white uppercase"
                            @click="showPlayerSelectionModal(1)"
                            :title="$t('player_selector.team_1')"
                        >
                            {{ $t('player_selector.team_1') }}
                        </h3>
                        <div class="flex items-start justify-center space-x-2">
                            <template v-for="index in maxTeamSize" :key="`team1-slot-${index}`">
                                <PlayerCard
                                    v-if="team1Players[index - 1]"
                                    :player="team1Players[index - 1]"
                                    :current-user-id="authStore.profile.id"
                                    :disabled="isUpdating"
                                    :clickable="true"
                                    :show-status="false"
                                    :image-title="$t('player_selector.remove_from_team')"
                                    :center-items="true"
                                    @click="handleRemovePlayerFromTeam"
                                    @remove="handleRemovePlayerFromTeam"
                                />
                                <div
                                    v-else-if="unassignedPlayers.length > 0"
                                    class="text-titles flex h-16 w-16 max-w-20 cursor-pointer flex-col items-center justify-center border border-dashed border-gray-500 hover:border-gray-400"
                                    @click="showPlayerSelectionModal(1)"
                                    :title="$t('player_selector.assign_to_team', { team: $t('player_selector.team_1') })"
                                >
                                    <span class="text-2xl">+</span>
                                </div>
                                <div
                                    v-else
                                    class="text-titles flex h-16 w-16 max-w-20 flex-col items-center justify-center border border-dashed border-gray-600 opacity-40"
                                >
                                    <span class="text-2xl">+</span>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div class="bg-stroke-1 relative w-px"></div>

                    <div class="flex-1 space-y-2">
                        <h3
                            class="cursor-pointer text-center text-sm font-bold text-white uppercase"
                            @click="showPlayerSelectionModal(2)"
                            :title="$t('player_selector.team_2')"
                        >
                            {{ $t('player_selector.team_2') }}
                        </h3>
                        <div class="flex items-start justify-center space-x-2">
                            <template v-for="index in maxTeamSize" :key="`team2-slot-${index}`">
                                <PlayerCard
                                    v-if="team2Players[index - 1]"
                                    :player="team2Players[index - 1]"
                                    :current-user-id="authStore.profile.id"
                                    :disabled="isUpdating"
                                    :clickable="true"
                                    :show-status="false"
                                    :image-title="$t('player_selector.remove_from_team')"
                                    :center-items="true"
                                    @click="handleRemovePlayerFromTeam"
                                    @remove="handleRemovePlayerFromTeam"
                                />
                                <div
                                    v-else-if="unassignedPlayers.length > 0"
                                    class="text-titles flex h-16 w-16 max-w-20 cursor-pointer flex-col items-center justify-center border border-dashed border-gray-500 hover:border-gray-400"
                                    @click="showPlayerSelectionModal(2)"
                                    :title="$t('player_selector.assign_to_team', { team: $t('player_selector.team_2') })"
                                >
                                    <span class="text-2xl">+</span>
                                </div>
                                <div
                                    v-else
                                    class="text-titles flex h-16 w-16 max-w-20 flex-col items-center justify-center border border-dashed border-gray-600 opacity-40"
                                >
                                    <span class="text-2xl">+</span>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div
                    v-if="(matchStore.setup.profiles || []).length === 0"
                    class="text-titles py-4 text-center"
                >
                    {{ $t('player_selector.no_players') }}
                </div>
            </div>
        </div>

        <div v-if="showValidationError && currentValidationMessage" class="px-4 pb-2">
            <div
                class="rounded border border-red-800 bg-red-900/20 p-2 text-center text-sm text-red-400"
            >
                {{ currentValidationMessage }}
            </div>
        </div>

        <PreviousNext :canProceed="canProceed" @next="nextStep" @previous="goBack" class="px-4" />
    </div>

    <shareQr
        v-if="shareQr"
        :model-value="shareQr"
        :url="shareQrUrl"
        @close="closeQrModal"
        @update:model-value="shareQr = $event"
    />

    <TeamAssignmentModal
        :is-visible="showTeamModal"
        :mode="modalMode"
        :player="selectedPlayerForTeam"
        :selected-team-number="selectedTeamForPlayers"
        :available-players="unassignedPlayers"
        :team1-count="team1Players.length"
        :team2-count="team2Players.length"
        :is-assigning="isUpdating"
        @assign-team="handleTeamAssignment"
        @assign-player="handlePlayerAssignment"
        @close="closeTeamModal"
    />

    <div
        v-if="showModeChangeConfirmation"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        @click.self="cancelModeChange"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('player_selector.switch_singles_title') }}
                    </h2>
                    <p class="text-sm text-gray-400">
                        {{ $t('player_selector.switch_singles_body') }}
                    </p>
                </div>

                <div class="mb-4 text-center">
                    <p class="text-sm text-red-400">
                        {{ assignedPlayersCount > 1
                            ? $t('player_selector.assigned_count_plural', { n: assignedPlayersCount })
                            : $t('player_selector.assigned_count_singular', { n: assignedPlayersCount }) }}
                    </p>
                </div>
            </div>

            <div class="border-stroke-1 mt-4 grid grid-cols-2 gap-4 border-t p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    @click="cancelModeChange"
                >
                    {{ $t('general.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    @click="confirmModeChange"
                >
                    {{ $t('player_selector.switch_singles_confirm') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>

    <div
        v-if="showLeaveConfirmation"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        @click.self="cancelGoBack"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('player_selector.leave_title') }}
                    </h2>
                    <p class="text-sm text-gray-400">
                        {{ $t('player_selector.leave_body') }}
                    </p>
                </div>

                <div class="mb-4 text-center">
                    <p class="text-sm text-red-400">
                        {{ (matchStore.setup.profiles?.length || 0) === 1
                            ? $t('player_selector.selected_count_singular', { n: matchStore.setup.profiles?.length || 0 })
                            : $t('player_selector.selected_count_plural', { n: matchStore.setup.profiles?.length || 0 }) }}
                    </p>
                </div>
            </div>

            <div class="border-stroke-1 mt-4 grid grid-cols-2 gap-4 border-t p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    @click="cancelGoBack"
                >
                    {{ $t('general.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton variant="primary" cut-position="left-top" @click="confirmGoBack">
                    {{ $t('player_selector.leave_confirm') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import { useFriends } from '@/composables/useFriends.js'
import { useTeamManagement } from '@/composables/useTeamManagement.js'
import { usePlayerValidation } from '@/composables/usePlayerValidation.js'
import PageHeader from '@/components/PageHeader.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Panel from '@/components/layout/Panel.vue'
import FriendSearchInput from '@/components/ui/FriendSearchInput.vue'
import FriendItem from '@/components/ui/FriendItem.vue'
import ShareQr from '@/components/ui/ShareQr.vue'
import PreviousNext from '@/components/ui/navigation/PreviousNext.vue'
import TeamAssignmentModal from '@/components/TeamAssignmentModal.vue'
import PlayerCard from '@/components/players/PlayerCard.vue'
import Avatar from '@/components/Avatar.vue'
import TabBar from '@/components/ui/TabBar.vue'
import { MATCH_TYPES } from '@/constants/matchTypes.js'

export default {
    name: 'PlayerSelectorView',
    components: {
        TabBar,
        Avatar,
        Panel,
        DiagonalCutButton,
        PageHeader,
        FriendSearchInput,
        FriendItem,
        ShareQr,
        PreviousNext,
        TeamAssignmentModal,
        PlayerCard,
    },
    setup() {
        const friendsComposable = useFriends()
        const teamManagement = useTeamManagement()
        const playerValidation = usePlayerValidation(teamManagement)

        return {
            ...friendsComposable,
            ...teamManagement,
            ...playerValidation,
        }
    },
    data() {
        return {
            activeTab: 'friends',
            shareQr: false,
            shareQrUrl: '',
            guestName: '',
            showLeaveConfirmation: false,
            loadingFriendId: null,
        }
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        tabs() {
            return [
                { id: 'friends', label: this.$t('player_selector.tab_friends') },
                { id: 'guest', label: this.$t('player_selector.tab_guest') },
                {
                    id: 'dartbot',
                    label: this.$t('player_selector.tab_dartbot'),
                    disabled: this.matchStore?.setup?.matchtype_id !== MATCH_TYPES.X01,
                },
            ]
        },

        stepInfo() {
            return this.matchStore.getStepInfo()
        },

        matchProfileIds() {
            return new Set((this.matchStore.setup.profiles || []).map((p) => p.profile_id || p.id))
        },

        sortedProfiles() {
            const profiles = this.matchStore.setup.profiles || []
            const creatorProfileId = this.matchStore.setup.creator_profile_id

            if (!creatorProfileId) return profiles

            return [...profiles].sort((a, b) => {
                const aIsCreator = a.profile_id === creatorProfileId
                const bIsCreator = b.profile_id === creatorProfileId

                if (aIsCreator) return -1
                if (bIsCreator) return 1
                return 0
            })
        },

        dartbots() {
            return [
                {
                    id: 1,
                    avatar: this.getBackendImageUrl('default-avatar-11.png'),
                    name: this.$t('player_selector.easy_bot_name'),
                    difficulty: this.$t('game_difficulty.beginner'),
                },
                {
                    id: 2,
                    avatar: this.getBackendImageUrl('default-avatar-12.png'),
                    name: this.$t('player_selector.medium_bot_name'),
                    difficulty: this.$t('game_difficulty.intermediate'),
                },
                {
                    id: 3,
                    avatar: this.getBackendImageUrl('default-avatar-13.png'),
                    name: this.$t('player_selector.hard_bot_name'),
                    difficulty: this.$t('game_difficulty.advanced'),
                },
            ]
        },

        maxTeamSize() {
            const totalPlayers = (this.matchStore.setup.profiles || []).length
            return Math.ceil(totalPlayers / 2)
        },

        team1IsFull() {
            return this.team1Players.length >= this.maxTeamSize
        },

        team2IsFull() {
            return this.team2Players.length >= this.maxTeamSize
        },
    },
    async mounted() {
        // Ensure match data is loaded
        const matchId = this.$route.params.matchId
        if (
            matchId &&
            this.matchStore.setup.id !== matchId &&
            this.matchStore.match.id !== matchId
        ) {
            try {
                await this.matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('Failed to restore match:', error)
                // Redirect on error
                this.$router.replace({ name: 'games' })
                return
            }
        }

        this.initializeTeams()
        this.matchStore.setCurrentStep(2)
    },
    methods: {
        getBackendImageUrl(imageName) {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/images/${imageName}`
        },
        checkPlayerLimits() {
            if (this.requiresTeamModeForMorePlayers) {
                this.$toast(this.$t('general.error'), 5000, this.getTeamModeRequiredMessage())
                return false
            }

            if (this.maxPlayersReached) {
                this.$toast(this.$t('general.error'), 5000, this.getMaxPlayersMessage())
                return false
            }

            return true
        },
        async addGuest() {
            if (!this.guestName.trim()) return

            if (!this.checkPlayerLimits()) return

            try {
                await this.matchStore.addGuestToMatch(this.guestName)
                this.guestName = ''
            } catch (error) {
                console.error('Failed to add guest:', error)
                this.$toast(
                    this.$t('player_selector.error_add_guest'),
                    5000,
                    error.message || this.$t('player_selector.error_add_guest'),
                )
            }
        },

        async addDartbot(bot) {
            if (!this.checkPlayerLimits()) return

            try {
                await this.matchStore.addDartbotToMatch(bot)
            } catch (error) {
                console.error('Failed to add dartbot:', error)
            }
        },

        async removeMatchProfile(id) {
            try {
                await this.matchStore.removeProfileFromMatch(id)
            } catch (error) {
                console.error('Failed to remove profile:', error)
                this.$toast(
                    this.$t('player_selector.error_generic'),
                    5000,
                    error.message || this.$t('general.error_retry'),
                )
            }
        },
        nextStep() {
            if (!this.canProceed) {
                this.showValidation()
                return
            }

            this.hideValidation()
            this.$router.push({
                name: 'match-setup-selector',
                params: { matchId: this.$route.params.matchId },
            })
        },
        async sendGameInviteToFriend(friend) {
            if (!this.checkPlayerLimits()) return

            this.loadingFriendId = friend.id
            try {
                await this.matchStore.addProfileToMatch(friend.id)
            } catch (error) {
                console.error('Failed to send game invite to friend:', error)
                this.$toast(
                    this.$t('player_selector.error_generic'),
                    5000,
                    error.message || this.$t('general.error_retry'),
                )
            } finally {
                this.loadingFriendId = null
            }
        },
        sendGameInvite() {
            console.log('Send game invite functionality to be implemented')
        },
        scanQRCode() {
            this.shareQrUrl = 'https://www.dartshopper.com/'
            this.shareQr = true
        },
        goBack() {
            if ((this.matchStore.setup.profiles || []).length > 0) {
                this.showLeaveConfirmation = true
                return
            }

            this.confirmGoBack()
        },

        async confirmGoBack() {
            console.log('Leaving match, clearing match data')
            try {
                const matchId = this.matchStore.setup.id
                if (matchId) {
                    await this.matchStore.cancelMatch(matchId)
                }
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            } catch (error) {
                console.error('Failed to cancel match:', error)
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            }
        },

        cancelGoBack() {
            this.showLeaveConfirmation = false
        },
        async setPlayMode(mode) {
            if (mode !== 'teams' && !this.canSwitchToSinglesMode) {
                this.$toast(this.$t('player_selector.error_generic'), 5000, this.getSinglesModeBlockedMessage())
                return
            }

            if (mode === 'singles' && this.playMode === 'teams' && this.assignedPlayersCount > 0) {
                this.openModeChangeConfirmation(mode)
                return
            }

            await this.performModeChange(mode)
        },

        async performModeChange(mode) {
            try {
                await this.switchPlayMode(mode)
                this.hideValidation()
            } catch (error) {
                this.$toast(
                    this.$t('player_selector.error_generic'),
                    5000,
                    error.message || this.$t('general.error_retry'),
                )
            }
        },

        showTeamAssignmentModal(player) {
            this.openTeamAssignmentModal(player)
        },

        showPlayerSelectionModal(teamNumber) {
            this.openPlayerSelectionModal(teamNumber, this.unassignedPlayers)
        },

        async handleTeamAssignment({ player, teamNumber }) {
            const teamUUID = this.getTeamUUID(teamNumber)
            if (teamUUID) {
                try {
                    await this.assignPlayerToTeam(player, teamUUID)
                    this.hideValidation()
                } catch (error) {
                    this.$toast(
                        this.$t('player_selector.error_generic'),
                        5000,
                        error.message || this.$t('general.error_retry'),
                    )
                }
            }
            this.closeTeamModal()
        },

        async handlePlayerAssignment({ player, teamNumber }) {
            const teamUUID = this.getTeamUUID(teamNumber)
            if (teamUUID) {
                try {
                    await this.assignPlayerToTeam(player, teamUUID)
                    this.hideValidation()
                } catch (error) {
                    this.$toast(
                        this.$t('player_selector.error_generic'),
                        5000,
                        error.message || this.$t('general.error_retry'),
                    )
                }
            }
            this.closeTeamModal()
        },

        async handleRemovePlayerFromTeam(playerOrId) {
            try {
                const player =
                    typeof playerOrId === 'object'
                        ? playerOrId
                        : this.matchStore.setup.profiles.find(
                              (p) => p.id === playerOrId || p.profile_id === playerOrId,
                          )

                if (!player) {
                    throw new Error(this.$t('player_selector.player_not_found'))
                }

                await this.removePlayerFromTeam(player)
                this.hideValidation()
            } catch (error) {
                this.$toast(
                    this.$t('player_selector.error_generic'),
                    5000,
                    error.message || this.$t('general.error_retry'),
                )
            }
        },

        async confirmModeChange() {
            if (this.pendingModeChange) {
                await this.performModeChange(this.pendingModeChange)
            }
            this.closeModeChangeConfirmation()
        },

        cancelModeChange() {
            this.closeModeChangeConfirmation()
        },

        closeQrModal() {
            this.shareQr = false
            this.shareQrUrl = ''
        },
    },
}
</script>
